import { ComponentNames as WnCComponentNames } from "lib/constant/component-constant";
import dynamic from "next/dynamic";

/** ---------------------------------------------------------------------------------
 * Core Components -- Load always ------------ */
const DtvFirstName = dynamic(
  () => import("@directv-digital/dds.ui.dtv-firstname")
);
const DtvPageSectionHeader = dynamic(
  () => import("@directv-digital/dds.ui.dtv-page-section-header")
);
const DtvVerticalSpacer = dynamic(
  () => import("@directv-digital/dds.ui.dtv-vertical-spacer")
);
const AccConversionForm = dynamic(
  () => import("src/components/acc-conversion-form/AccConversionForm")
);
const DtvInPageLegal = dynamic(
  () => import("@directv-digital/dds.ui.dtv-in-page-legal")
);
// WAT-12021: use the same local component as the Pages canvas. The published
// package lags it (no countdown timer, image alt props or Tangerine CTA
// stacking), so content cards rendered differently on App Router pages.
const ContentCards = dynamic(
  () => import("@/video-lab/dtv-content-cards")
);
const DtvHero = dynamic(() => import("@directv-digital/dds.ui.dtv-hero"));

const DtvRevisitorModal = dynamic(
  () => import("@/video-lab/dtv-revisitor-modal/dtv-revisitor-modal").then(
    mod => mod.DtvRevisitorModal
  )
);

/** ---------------------------------------------------------------------------------
 * Channel/GP Tools (maintain as relative imports, publish to VL as needed) ------------- */
const ChnlListingPage = dynamic(
  () => import("src/components/ChnlListingPageV2")
);
const DtvAgentPackageCompareTool = dynamic(
  () => import("src/components/dtv-agent-package-compare-tool")
);
const ChlupInteractiveToolV2 = dynamic(
  () => import("src/components/ChlupInteractiveToolV2")
);
const LocalRsnTool = dynamic(
  () => import("src/components/video-lab/local-rsn-tool")
);
const RsnLookupTool = dynamic(
  () => import("src/components/video-lab/rsn-lookup-tool")
);
const RsnFeeLookup = dynamic(
  () => import("src/components/video-lab/rsn-fee-lookup")
);
const LocalChannelsLookupPage = dynamic(
  () => import("src/components/LocalChannelsLookupPage")
);
const PackageCarousel = dynamic(
  () => import("@/video-lab/package-carousel/package-carousel")
);
const FlipCards = dynamic(
  () => import("@/video-lab/flip-cards").then(mod => ({ default: mod.FlipCards }))
);

/** ---------------------------------------------------------------------------------
 * Components (maintain as a video lab import, unless otherwise needed) ------------
 * Please try to keep me alphabetized :) ------------------------------------------- */
const AmbientVideoMarquee = dynamic(
  () => import("@/video-lab/ambient-video-marquee")
);
const ChannelLogoCarousal = dynamic(
  () => import("@directv-digital/dds.ui.channel-logo-carousal")
);
const DishPointer = dynamic(
  // Note: UF WnC Canvas only (No StreamTV)
  () => import("@directv-digital/dds.ui.dtv-dish-pointer")
);
const Dtv4UpIconList = dynamic(
  () => import("@directv-digital/dds.ui.dtv-four-up-icon-list")
);
const DtvAgnosticPlanCards = dynamic(
  () => import("@directv-digital/dds.ui.dtv-agnostic-plan-cards")
);
const DtvAppDownloadBanner = dynamic(
  () => import("@/video-lab/dtv-app-download-banner")
);
const DtvBreadCrumbs = dynamic(
  () => import("@directv-digital/dds.ui.dtv-breadcrumbs")
);
const DtvCompetitorTableModule = dynamic(
  () => import("@/video-lab/competitor-table")
);
const DtvCountdown = dynamic(
  () => import("@directv-digital/dds.ui.dtv-countdown")
);
const DtvEmailCapture = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture")
);
const DtvEmailModal = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture-popup-modal")
);
const DtvExitModal = dynamic(
  () => import("src/components/video-lab/dtv-exit-modal")
);
const MarketingAiModal = dynamic(
  () => import("@/video-lab/marketing-ai-modal")
);
const DtvExternalScriptLoader = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-script-loader")
);
const DtvExternalServiceComparison = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-service-comparison-chart")
);
const DtvExternalServiceComparisonChartTabs = dynamic(
  () => import(
    "@/video-lab/dtv-external-service-comparison-chart-tabs"
  )
);
const DtvFaq = dynamic(() => import("@directv-digital/dds.ui.dtv-faq"));
const DtvFormBuilder = dynamic(
  () => import("@directv-digital/dds.ui.dtv-form-builder")
);
const DtvFindRetailer = dynamic(
  () => import("@directv-digital/dds.ui.dtv-find-retailer")
);
const DtvFloatingwidget = dynamic(
  () => import("@directv-digital/dds.ui.dtv-floating-widget-v3")
);
const DtvHeadLine = dynamic(
  () => import("@directv-digital/dds.ui.dtv-headline")
);
const DtvInsider = dynamic(() => import("@directv-digital/dds.ui.dtv-insider"));
const DtvInternalProductComparisionChart = dynamic(
  () => import("@directv-digital/dds.ui.dtv-internal-product-comparison-chart")
);
const DtvLinkForest = dynamic(
  () => import("@directv-digital/dds.ui.link-forest")
);
const DtvListItem = dynamic(() => import("@directv-digital/dds.ui.dtv-list"));
const DtvLongFormatCard = dynamic(
  () => import("@directv-digital/dds.ui.dtv-longformat-card")
);
const DtvMduHero = dynamic(
  () => import("@/video-lab/dtv-mdu-hero")
);
// WAT-12021: use the same local component as the Pages canvas. The published
// package lags it (no image-height cap), so offer-card images sized differently
// on App Router pages than on Pages Router.
const DtvOfferCards = dynamic(
  () => import("@/video-lab/dtv-offer-cards/dtv-offer-cards").then(mod => mod.DtvOfferCards)
);
const DtvQuizComponent = dynamic(
  () => import("@directv-digital/dds.ui.dtv-quiz-component")
);
const DtvThreeUp = dynamic(
  () => import("@directv-digital/dds.ui.dtv-three-up")
);
const EquipmentWizard = dynamic(
  () => import("@/video-lab/equipment-wizard")
);
const featuredArticleList = dynamic(
  () => import("@/video-lab/dtv-featured-article-list")
);
const ImageMarquee = dynamic(
  () => import("@directv-digital/dds.ui.image-marquee")
);
const InPageNav = dynamic(() => import("@directv-digital/dds.ui.inpagenav"));
const MarqueeCarousel = dynamic(
  () => import("@directv-digital/dds.ui.marquee-carousel")
);
const MediaModule = dynamic(
  () => import("@directv-digital/dds.ui.dtv-media-module")
);
const MessagingBanner = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-banner")
);
const MessagingCard = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-card")
);
const PackageComparisonSavingsCalculator = dynamic(
  () => import("@directv-digital/dds.ui.package-comparison-calculator")
);
const PlanPortal = dynamic(() => import("@directv-digital/dds.ui.plan-portal"));
const ReviewCarousel = dynamic(
  () => import("src/components/video-lab/review-carousel")
);
const RotatingMarque = dynamic(
  () => import("@directv-digital/dds.ui.rotating-marque")
);
const DtvRowCanvas = dynamic(
  () => import("@directv-digital/dds.ui.dtv-row-canvas")
);
const ScrollToTop = dynamic(
  () => import("@directv-digital/dds.ui.scroll-to-top")
);
const SearchComponent = dynamic(
  () => import("@directv-digital/dds.ui.search-component")
);
const SearchWatchNow = dynamic(
  () => import("@directv-digital/dds.ui.search-guide")
);
const SeoContent = dynamic(() => import("@directv-digital/dds.ui.seo-content"));
const SiteMap = dynamic(() => import("@/video-lab/dtv-sitemap"));
const ShowCarousal = dynamic(
  () => import("src/components/video-lab/show-carousal")
);
const TvPromise = dynamic(() => import("@directv-digital/dds.ui.tv-promise"));
const UcgGalleryLoader = dynamic(
  () => import("@directv-digital/dds.ui.ucg-gallery-loader")
);
const VerticalVideoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.vertical-video-carousel")
);
const VideoMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-marquee")
);
const videoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.video-carousel")
);
const VideoMarqueeCampaign = dynamic(
  () => import("@directv-digital/dds.ui.video-marquee-campaign")
);
const VideoPopUpMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-popup-marquee")
);
const YotpoPackageReview = dynamic(
  () => import("@/video-lab/yotpo-package-review")
);
const YotpoPackageRating = dynamic(
  () => import("@/video-lab/yotpo-package-rating")
);
const DtvMarquee = dynamic(() => import("@directv-digital/dds.ui.dtv-marquee"));

/**
 * Fusion components
 */
const ContentCardC = dynamic(
  () => import("@directv-digital/fusion.organisms.content-card-c")
);

/** ---------------------------------------------------------------------------------
 * Components (maintain as relative imports, publish to VL as needed) ------------- */
const ConspiracyBoard = dynamic(
  () => import("src/components/video-lab/conspiracy-board")
);
const DtvPlanCardModule = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-plan-card-v2")
);
const DtvHorizontalPlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-horizontal-plan-card")
);
const DtvTogglePlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-toggle-plan-card")
);
const Iframe = dynamic(() => import("src/components/iframe"));
const FlappyPigeon = dynamic(
  () => import("src/components/flappy-pigeon/components/flappy-pigeon")
);
const PackageCompareTool = dynamic(
  () => import("@/video-lab/package-compare-tool")
);
const ChannelPackLineup = dynamic(
  () => import("@/video-lab/channel-pack-lineup")
);
const InPageChannelPackLineup = dynamic(
  () => import("@/video-lab/inpage-channel-pack-lineup")
);
const GenrePreviewTool = dynamic(
  () => import("@/video-lab/genre-preview-tool")
);
const AiProductRecommender = dynamic(
  () => import("@/video-lab/ai-product-recommender")
);
const MediaCardImage1Up2UpV2 = dynamic(
  () => import("@/video-lab/media-module-v2/media-module-image")
);
const GenrePackDetailsPanel = dynamic(
  () => import("@/video-lab/genre-pack-details-panel/genre-pack-details-panel")
);
const DtvCompetitorComparisonTable = dynamic(
  () => import("@/video-lab/dtv-competitor-comparison-table")
);
const MarketingBanner = dynamic(() => import("@directv-digital/dds.ui.marketing-banner"));

const CustomerAcqCompareTool = dynamic(
  () => import("src/components/custom-acq-compare-tool")
);

const ChannelCompareToolV3 = dynamic(
  () => import("@/video-lab/channel-compare-tool-v3")
);

export const ComponentMap: { [key: string]: any } = {
  [WnCComponentNames.INPAGENAV]: InPageNav,
  [WnCComponentNames.DTVMARQUEE]: DtvMarquee,
  [WnCComponentNames.DTVCAROUSEL]: MarqueeCarousel,
  [WnCComponentNames.ROTATINGMARQUEE]: RotatingMarque,
  [WnCComponentNames.VIDEOMARQUEE]: VideoMarquee,
  [WnCComponentNames.IMAGEMARQUEE]: ImageMarquee,
  [WnCComponentNames.VIDEOPOPUPMARQUEE]: VideoPopUpMarquee,
  [WnCComponentNames.VIDEOCAROUSEL]: videoCarousel,
  [WnCComponentNames.DISHPOINTER]: DishPointer,
  [WnCComponentNames.LONGFORMATCARD]: DtvLongFormatCard,
  [WnCComponentNames.CONTENTCARDS]: ContentCards,
  [WnCComponentNames.PAGESECTIONHEADER]: DtvPageSectionHeader,
  [WnCComponentNames.PLANCARD]: DtvPlanCardModule,
  [WnCComponentNames.HORIZONTAL_PLAN_CARD]: DtvHorizontalPlanCard,
  [WnCComponentNames.TOGGLE_PLAN_CARD]: DtvTogglePlanCard,
  [WnCComponentNames.FAQ]: DtvFaq,
  [WnCComponentNames.INPAGELEGAL]: DtvInPageLegal,
  [WnCComponentNames.BREADCRUMBSV2]: DtvBreadCrumbs,
  [WnCComponentNames.INTERNALPRODCUTCOMPARISIONCHART]:
    DtvInternalProductComparisionChart,
  [WnCComponentNames.DTVHERO]: DtvHero,
  [WnCComponentNames.VERTICALSPACER]: DtvVerticalSpacer,
  [WnCComponentNames.ACCCONVERSIONFORM]: AccConversionForm,
  [WnCComponentNames.HEADLINE]: DtvHeadLine,
  [WnCComponentNames.LIST_ITEM]: DtvListItem,
  [WnCComponentNames.FOUR_UP_ICON_LIST]: Dtv4UpIconList,
  [WnCComponentNames.MESSAGINGBANNER]: MessagingBanner,
  [WnCComponentNames.MESSAGINGCARD]: MessagingCard,
  [WnCComponentNames.EXTERNALSERVICECOMPARISON]: DtvExternalServiceComparison,
  [WnCComponentNames.EXTERNALSERVICECOMPARISONTABS]:
    DtvExternalServiceComparisonChartTabs,
  [WnCComponentNames.DTV1UP2UPMEDIA]: MediaModule,
  [WnCComponentNames.DTVAGNOSTICPLANCARDS]: DtvAgnosticPlanCards,
  [WnCComponentNames.DTVEXTERNALSCRIPTLOADER]: DtvExternalScriptLoader,
  [WnCComponentNames.DTVMDUHERO]: DtvMduHero,
  [WnCComponentNames.DTVYOTPOGALLERYWRAPPER]: UcgGalleryLoader,
  [WnCComponentNames.FLOATINGWIDGETV2]: DtvFloatingwidget,
  [WnCComponentNames.FORM_BUILDERV2]: DtvFormBuilder,
  [WnCComponentNames.QUIZV2]: DtvQuizComponent,
  [WnCComponentNames.EMAILPOPUPMODAL]: DtvEmailModal,
  [WnCComponentNames.EMAILCAPTUREV2]: DtvEmailCapture,
  [WnCComponentNames.COMPETITORTABLE]: DtvCompetitorTableModule,
  [WnCComponentNames.DTVIFRAME]: Iframe,
  [WnCComponentNames.ROWCANVASV2]: DtvRowCanvas,
  [WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2]: ChannelCompareToolV3,
  [WnCComponentNames.CHANNEL_LISTING_PAGE]: ChnlListingPage,
  [WnCComponentNames.DTVAGENTPACKAGECOMPARETOOL]: DtvAgentPackageCompareTool,
  [WnCComponentNames.SATELLITE_CHANNEL_LISTING_PAGE]: ChnlListingPage,
  [WnCComponentNames.INSIDERARTICLE]: DtvInsider,
  [WnCComponentNames.SITEMAP]: SiteMap,
  [WnCComponentNames.CHANNELLOGOCAROUSAL]: ChannelLogoCarousal,
  [WnCComponentNames.DTVFIRSTNAME]: DtvFirstName,
  [WnCComponentNames.DTVTHREEUP]: DtvThreeUp,
  [WnCComponentNames.LINKFOREST]: DtvLinkForest,
  [WnCComponentNames.CHLUP_INTERACTIVE_TOOL]: ChlupInteractiveToolV2,
  [WnCComponentNames.PACKAGECOMPARISONCALCULATOR]:
    PackageComparisonSavingsCalculator,
  [WnCComponentNames.LOCALCHANNELSLOOKUPPAGE]: LocalChannelsLookupPage,
  // [WnCComponentNames.REFERENCECOMPONENT]: ReferenceComponent,
  [WnCComponentNames.FINDRETAILER]: DtvFindRetailer,
  [WnCComponentNames.SEARCHCOMPONENT]: SearchComponent,
  [WnCComponentNames.MARKETINGBANNER]: MarketingBanner,
  [WnCComponentNames.SHOWCAROUSAL]: ShowCarousal,
  [WnCComponentNames.OFFERCARDS]: DtvOfferCards,
  [WnCComponentNames.SEARCHWATCHNOW]: SearchWatchNow,
  [WnCComponentNames.SEOCONTENT]: SeoContent,
  [WnCComponentNames.DTVCOUNTDOWN]: DtvCountdown,
  [WnCComponentNames.TVPROMISE]: TvPromise,
  [WnCComponentNames.EXITMODAL]: DtvExitModal,
  [WnCComponentNames.MARKETINGAIMODAL]: MarketingAiModal,
  [WnCComponentNames.VIDEOMARQUEECAMPAIGN]: VideoMarqueeCampaign,
  [WnCComponentNames.FLAPPYPIGEON]: FlappyPigeon,
  [WnCComponentNames.SCROLLTOTOP]: ScrollToTop,
  [WnCComponentNames.RSNLOOKUPTOOL]: RsnLookupTool,
  [WnCComponentNames.RSNFEELOOKUP]: RsnFeeLookup,
  [WnCComponentNames.REVIEWCAROUSEL]: ReviewCarousel,
  [WnCComponentNames.APPDOWNLOADBANNER]: DtvAppDownloadBanner,
  [WnCComponentNames.VERTICALVIDEOCAROUSEL]: VerticalVideoCarousel,
  [WnCComponentNames.LOCALRSNTOOL]: LocalRsnTool,
  [WnCComponentNames.YOTPOPACKAGEREVIEWWIDGET]: YotpoPackageReview,
  [WnCComponentNames.PLANPORTAL]: PlanPortal,
  [WnCComponentNames.CONSPIRACYBOARD]: ConspiracyBoard,
  [WnCComponentNames.PACKAGECOMPARETOOL]: PackageCompareTool,
  [WnCComponentNames.CHANNELPACKLINEUP]: ChannelPackLineup,
  [WnCComponentNames.INPAGECHANNELPACKLINEUP]: InPageChannelPackLineup,
  [WnCComponentNames.GENREPREVIEWTOOL]: GenrePreviewTool,
  [WnCComponentNames.AIPRODUCTRECOMMENDER]: AiProductRecommender,
  [WnCComponentNames.EQUIPMENTWIZARD]: EquipmentWizard,
  [WnCComponentNames.FEATUREDARTICLELIST]: featuredArticleList,
  [WnCComponentNames.AMBIENTVIDEOMARQUEE]: AmbientVideoMarquee,
  [WnCComponentNames.YOTPOPACKAGERATING]: YotpoPackageRating,
  [WnCComponentNames.MEDIA_CARD_IMAGE_1UP_2UP_V2]: MediaCardImage1Up2UpV2,
  [WnCComponentNames.REVISITORMODAL]: DtvRevisitorModal,
  [WnCComponentNames.PACKAGECAROUSEL]: PackageCarousel,
  [WnCComponentNames.FLIPCARDS]: FlipCards,
  [WnCComponentNames.GENREPACKDETAILSPANEL]: GenrePackDetailsPanel,
  [WnCComponentNames.COMPETITORCOMPARISONTABLEV2]: DtvCompetitorComparisonTable,
  [WnCComponentNames.CONTENTCARDC]: ContentCardC,
  [WnCComponentNames.CUSTOMERACQCOMPARETOOL]: CustomerAcqCompareTool
};

export const componentKeyMap = Object.keys(ComponentMap);
